import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { db } from "@/firebase/config";
import { collection, getDocs, query } from "firebase/firestore";
import GuardianHeader from "./GuardianHeader";
import GuardianSidebar from "./GuardianSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { 
  Heart, 
  Calendar, 
  MessageCircle, 
  TrendingUp,
  Baby,
  Award,
  Clock,
  Bell,
  Car,
  Utensils,
  BookMarked
} from "lucide-react";
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import ContentLoader from "../authentication/ContentLoader";
import EnrollmentRequiredBanner from "../components/EnrollmentRequiredBanner";

const childWellness = [
  {
    icon: Heart,
    title: "Child's Wellbeing",
    value: "Excellent",
    change: "Happy & Healthy",
    color: "bg-pink-500"
  },
  {
    icon: Calendar,
    title: "Attendance Rate",
    value: "96.5%",
    change: "Above school average",
    color: "bg-blue-500"
  },
  {
    icon: MessageCircle,
    title: "Teacher Notes",
    value: "3 New",
    change: "All positive feedback",
    color: "bg-green-500"
  },
  {
    icon: Award,
    title: "Monthly Stars",
    value: "28",
    change: "Great improvement!",
    color: "bg-yellow-500"
  }
];

const academicProgressData = [
  { month: "Jan", reading: 75, math: 68, social: 82 },
  { month: "Feb", reading: 78, math: 72, social: 85 },
  { month: "Mar", reading: 82, math: 75, social: 88 },
  { month: "Apr", reading: 85, math: 78, social: 90 },
  { month: "May", reading: 88, math: 82, social: 92 },
  { month: "Jun", reading: 91, math: 85, social: 95 },
];

const timeDistribution = [
  { name: "Learning", value: 40, fill: "#3B82F6" },
  { name: "Play", value: 25, fill: "#10B981" },
  { name: "Meals", value: 15, fill: "#F59E0B" },
  { name: "Rest", value: 20, fill: "#8B5CF6" },
];

const chartConfig = {
  reading: { label: "Reading", color: "#3B82F6" },
  math: { label: "Mathematics", color: "#10B981" },
  social: { label: "Social Skills", color: "#F59E0B" }
};

interface PieLabelProps {
  name?: string;
  percent?: number;
  cx?: string | number | undefined; // Updated to match PieLabelRenderProps
  cy?: string | number | undefined; // Updated to match PieLabelRenderProps
  midAngle?: string | number | undefined; // Updated to match PieLabelRenderProps
  innerRadius?: string | number | undefined; // Updated to match PieLabelRenderProps
  outerRadius?: string | number | undefined; // Updated to match PieLabelRenderProps
  value?: number;
}

const GuardianDashboard = () => {
  const user = useSelector((state: any) => state.users?.currentUser || {});
  const { id: userId, school, selectedStudent } = user;
  const [dorms, setDorms] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log("Current user state in GuardianDashboard:", user);
    if (!userId) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No userId found, skipping dorm fetch`);
      setIsLoading(false);
      return;
    }

    const fetchDorms = async () => {
      // Strictly use selectedStudent's schoolID for guardians
      const targetSchoolID = selectedStudent?.schoolID;

      if (!targetSchoolID) {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] GuardianDashboard: No schoolID assigned to selected student (or no student linked).`);
        setIsLoading(false);
        return;
      }

      try {
        const dormsQuery = query(collection(db, `schools/${targetSchoolID}/dorms`));
        const dormsSnapshot = await getDocs(dormsQuery);
        const dormsList = dormsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (dormsList.length > 0) {
          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Guardian access verified - Dorms fetched for user ${userId} (schoolID: ${targetSchoolID}):`, dormsList);
        } else {
          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No dorms found for schoolID: ${targetSchoolID} for user ${userId}`);
        }
        setDorms(dormsList);
      } catch (error) {
        console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching dorms for user ${userId}:`, error);
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Guardian access denied or data unavailable for user ${userId}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDorms();
  }, [userId, school?.schoolID, selectedStudent?.schoolID]);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <GuardianSidebar />
        <div className="flex flex-col flex-1 h-full w-full">
          <GuardianHeader />
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900">
            <ContentLoader isLoading={isLoading} message="">
              {!selectedStudent?.schoolID ? (
                <EnrollmentRequiredBanner role="guardian" />
              ) : (
                <>
                  <div className="mb-4 sm:mb-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                      {selectedStudent?.firstName ? `${selectedStudent.firstName}'s Journey` : "My Child's Journey"}
                    </h1>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                      Track your child's growth, happiness, and development
                    </p>
                  </div>
                  
                  {/* Child Wellness Stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
                    {childWellness.map((stat, index) => (
                      <Card key={index} className="hover:shadow-lg transition-all duration-300 dark:bg-gray-800 dark:border-gray-700">
                        <CardHeader className="pb-2 p-3 sm:p-6">
                          <div className="flex items-center justify-between">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                              <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          </div>
                          <CardTitle className="text-base sm:text-lg dark:text-white">{stat.title}</CardTitle>
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-300">{stat.change}</div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>

                  {/* Child Development Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
                    <Card className="dark:bg-gray-800 dark:border-gray-700">
                      <CardHeader className="p-3 sm:p-6">
                        <CardTitle className="flex items-center gap-2 dark:text-white">
                          <BookMarked className="h-5 w-5 text-blue-500" />
                          Learning Development
                        </CardTitle>
                        <CardDescription className="dark:text-gray-300">Your child's academic growth across key areas</CardDescription>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-6 pt-0">
                        <ChartContainer config={chartConfig} className="h-[250px] w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={academicProgressData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="month" fontSize={10} />
                              <YAxis fontSize={10} />
                              <ChartTooltip content={<ChartTooltipContent />} />
                              <Line type="monotone" dataKey="reading" stroke="#3B82F6" strokeWidth={2} />
                              <Line type="monotone" dataKey="math" stroke="#10B981" strokeWidth={2} />
                              <Line type="monotone" dataKey="social" stroke="#F59E0B" strokeWidth={2} />
                            </LineChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      </CardContent>
                    </Card>

                    <Card className="dark:bg-gray-800 dark:border-gray-700">
                      <CardHeader className="p-3 sm:p-6">
                        <CardTitle className="flex items-center gap-2 dark:text-white">
                          <Clock className="h-5 w-5 text-purple-500" />
                          Daily Activities
                        </CardTitle>
                        <CardDescription className="dark:text-gray-300">How your child spends time at school</CardDescription>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-6 pt-0">
                        <ChartContainer config={chartConfig} className="h-[250px] w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={timeDistribution}
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                dataKey="value"
                                label={(props: PieLabelProps): React.ReactNode => {
                                  const { name, percent, value } = props;
                                  // Use value if percent is undefined, assuming value is always defined in timeDistribution
                                  const displayPercent = percent !== undefined ? (percent * 100).toFixed(0) : ((value || 0) / 100).toFixed(0);
                                  return name ? `${name} ${displayPercent}%` : `${displayPercent}%`;
                                }}
                              >
                                {timeDistribution.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <ChartTooltip content={<ChartTooltipContent />} />
                            </PieChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Today's Highlights */}
                  <Card className="dark:bg-gray-800 dark:border-gray-700">
                    <CardHeader className="p-3 sm:p-6">
                      <CardTitle className="flex items-center gap-2 dark:text-white">
                        <Bell className="h-5 w-5 text-orange-500" />
                        Today's Highlights
                      </CardTitle>
                      <CardDescription className="dark:text-gray-300">Special moments and important updates</CardDescription>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6 pt-0">
                      <div className="space-y-4">
                        <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <Baby className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-blue-800 dark:text-blue-200">Made a new friend today!</p>
                            <p className="text-sm text-blue-600 dark:text-blue-300">Your child played with Emma during recess</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">10:30 AM</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <Utensils className="h-5 w-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-green-800 dark:text-green-200">Healthy lunch consumed</p>
                            <p className="text-sm text-green-600 dark:text-green-300">Ate all vegetables and asked for seconds!</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">12:15 PM</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                          <Car className="h-5 w-5 text-purple-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-purple-800 dark:text-purple-200">Bus Reminder</p>
                            <p className="text-sm text-purple-600 dark:text-purple-300">Pick-up at 3:15 PM - Gate B</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Scheduled</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Dorms Section */}
                  <div className="mt-6">
                    <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Dorms</h2>
                    {dorms.length > 0 ? (
                      <ul className="list-disc pl-5">
                        {dorms.map((dorm) => (
                          <li key={dorm.id} className="mb-2 text-gray-700 dark:text-gray-300">
                            {dorm.dormName} (Capacity: {dorm.capacity}, Available: {dorm.available}, Patron: {dorm.patronTeacherId || "None"})
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-300">No dorms available for this school.</p>
                    )}
                  </div>
                </>
              )}
            </ContentLoader>
            <Outlet />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default GuardianDashboard;